"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/tentang-kami/ - tempat berkas INI berada.
     ../components, ../i18n, ../data  -> naik satu tingkat ke app/ */
import ContactBanner from "../components/ContactBanner";
import Footer from "../components/Footer";
import { useLanguage } from "../i18n/LanguageProvider";
import { BAGIAN_TENTANG } from "../data/tentang";

/* ============================================================
   HALAMAN INDEKS TENTANG KAMI - /tentang-kami

   Bentuknya sengaja mengikuti /layanan: kepala halaman, lalu kartu
   bergambar untuk tiap bagian. Dua halaman indeks yang berperilaku
   sama membuat situs terasa satu bangunan, bukan kumpulan halaman
   yang dikerjakan terpisah.

   Bedanya cuma jumlah kolom. Layanan ada lima belas, jadi empat kolom
   di layar lebar. Di sini hanya tiga, jadi tiga kolom - empat kolom
   akan menyisakan satu kolom kosong yang terlihat seperti ada kartu
   yang gagal dimuat.

   ============================================================
   SATU TEKS MEMAKAI TEKS LOKAL, SISANYA DARI dictionary.js

   Judul, deskripsi, dan label tombol semuanya memakai kunci yang SUDAH
   ADA di dictionary.js - berkas ini tidak menuntut satu pun kunci baru.

   Kecuali kalimat pengantar di bawah judul. Tidak ada kunci yang
   cocok, dan menambah satu kunci ke berkas 1100 baris demi satu
   kalimat yang hanya dipakai di sini tidak sepadan. Ia ditulis sebagai
   objek { id, en }, pola yang sudah dipakai IgdContent, MCUContent,
   LaboratoriumContent, SwabPcrContent, dan not-found.jsx.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah beberapa
   baris ikut membawa \r\n ke dalam nilai atribut dan memicu hydration
   error. Kalau perlu dipecah, pakai ["...", "..."].join(" ").
   ============================================================ */

const TEKS = {
  sub: {
    id: "Profil rumah sakit, dokumentasi fasilitas, dan materi edukasi dari dokter kami. Pilih salah satu untuk melihat detailnya.",
    en: "Our hospital profile, photos of our facilities, and education materials from our doctors. Select one to see the details.",
  },
};

function pilihTeks(nilai, lang) {
  return nilai[lang] || nilai.id;
}

/* ============================================================
   RUANG DI SEKELILING GAMBAR "contain"

   ⚠️ SATU ANGKA UNTUK SEMUA KARTU TIDAK BEKERJA, dan alasannya
   rasio - bukan selera.

   Gambar "contain" berhenti membesar begitu salah satu sisinya
   menyentuh bingkai. Sisi MANA yang menyentuh duluan ditentukan
   rasionya dibanding bingkai 4:3 (1,33):

     logo.png         2,43 - jauh lebih melebar, LEBARNYA yang mentok
     gedung, poster   lebih dekat ke 4:3, tingginya ikut terpakai

   Artinya padding yang sama menghasilkan sisa ruang yang jauh
   berbeda: logo terlihat berdesakan di kiri-kanan sementara yang
   lain sudah punya ruang sendiri dari letterbox-nya.

   ⚠️ CLASS BIASA, BUKAN NILAI ARBITRER. p-8/p-10/p-12/p-16 ada di
   skala bawaan Tailwind. Nilai arbitrer seperti p-[52px] beberapa
   kali tidak terbentuk di proyek ini dan gagalnya diam-diam - tidak
   ada galat, paddingnya cuma tidak ada.

   Diatur lewat medan `ruang` di app/data/tentang.js. Yang tidak
   mengisinya jatuh ke "sedang". */
const RUANG = {
  sedang: "p-8 md:p-10",
  lega: "p-12 md:p-16",
};

export default function TentangContent() {
  const { t, lang } = useLanguage();

  /* Padding atas = tinggi header fixed, angkanya disalin dari
     app/page.jsx. Kalau tinggi di Header.jsx berubah, semua halaman
     yang memakai angka ini harus ikut diubah. */
  return (
    <div className="relative w-full overflow-x-hidden bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BARIS BREADCRUMB ----------
         Pita #f1faf5 selebar layar, sama dengan yang dipakai halaman
         About dan Gallery - supaya ketiganya terasa satu rangkaian. */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div className="mx-auto max-w-[1600px] px-4 py-3 sm:px-6 sm:py-3.5 lg:px-10">
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]">
                  {t("header.nav.home")}
                </Link>
              </li>

              <li className="flex items-center">
                {/* Pemisah digambar sebagai SVG ber-aria-hidden, BUKAN
                   karakter ">". Kalau ditulis sebagai teks, pembaca
                   layar membacakan "lebih besar dari" di antara tiap
                   item. */}
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
                  <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {t("header.menu.tentang.label")}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <section className="px-4 pb-16 pt-10 sm:px-6 md:pb-24 md:pt-14 lg:px-10">
        <div className="mx-auto max-w-[1600px]">

          <div className="mb-8 max-w-3xl sm:mb-10 md:mb-14">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em] md:text-sm">
              {t("about.eyebrow")}
            </p>

            {/* h1 memakai NAMA RUMAH SAKITNYA, bukan "Tentang Kami".

               Kata "Tentang Kami" sudah muncul dua kali di layar ini -
               di breadcrumb dan di eyebrow di atas. Mengulanginya
               ketiga kali sebagai judul tidak menambah apa pun,
               sementara nama lengkap rumah sakit berguna baik untuk
               pengunjung yang baru pertama datang maupun untuk mesin
               pencari. */}
            <h1 className="mb-3 text-balance text-2xl font-bold leading-[1.1] tracking-tight text-[var(--c-ink)] sm:text-3xl md:mb-5 md:text-5xl">
              {t("about.judul")}
            </h1>

            <p className="text-pretty text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:text-lg">
              {pilihTeks(TEKS.sub, lang)}
            </p>
          </div>

          {/* Tiga kolom di lg, dan berhenti di situ. Empat kolom akan
             menyisakan satu kolom kosong yang terbaca seperti kartu
             yang gagal dimuat. */}
          <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8">
            {BAGIAN_TENTANG.map((bagian) => (
              <li key={bagian.key} className="h-full">
                <KartuBagian bagian={bagian} t={t} />
              </li>
            ))}
          </ul>

        </div>
      </section>

      <section id="kontak" className="scroll-mt-24 md:scroll-mt-32">
        <ContactBanner />
      </section>

      <Footer />

    </div>
  );
}

/* ============================================================
   KARTU

   SELURUH KARTU adalah tautannya, bukan hanya tulisan "Selengkapnya"
   di bawah. Sasaran sentuh yang jauh lebih besar di ponsel, dan
   pembaca layar mendengar satu tautan bernama "About" - bukan tiga
   tautan yang semuanya bernama "Selengkapnya".

   Ketiga alamatnya sudah ada, jadi di sini tidak diperlukan penanda
   `siap` seperti pada app/data/layanan.js.
   ============================================================ */

function KartuBagian({ bagian, t }) {
  const Icon = bagian.Icon;

  /* GAMBAR YANG GAGAL DIMUAT.

     Percabangan di bawah hanya memeriksa apakah `image` bernilai null -
     ia TIDAK tahu apakah berkasnya benar-benar bisa dimuat. Tanpa
     penanda ini, path yang salah menghasilkan kotak kosong berikon
     gambar rusak, bukan fallback ke ikon. */
  const [gagal, setGagal] = useState(false);
  const adaFoto = Boolean(bagian.image) && !gagal;

  /* Ditentukan di app/data/tentang.js. Lihat catatan field `muat` di
     sana untuk alasan lengkapnya. */
  const muatkan = bagian.muat === "contain";

  /* Padding hanya berlaku untuk gambar "contain". Pada object-cover
     gambarnya memang harus memenuhi bingkai - memberinya padding
     berarti menyisakan bidang kosong yang terbaca seperti gambar
     yang gagal memuat. */
  const kelasRuang = muatkan ? RUANG[bagian.ruang] || RUANG.sedang : "";

  return (
    <Link
      href={bagian.href}
      className="group flex h-full flex-col overflow-hidden rounded-2xl bg-[var(--c-surface)] ring-1 ring-[var(--c-line)] transition duration-300 hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] motion-reduce:transition-none"
    >
      {/* ⚠️ LATARNYA PUTIH, DAN GARIS BAWAHNYA WAJIB IKUT.

         Sebelumnya bidang ini bergradasi hijau muda
         (from-[var(--c-tint)] to-[var(--c-line-soft)]). Gradasi itu
         hanya pernah terlihat di SATU kartu - kartu About, yang
         gambarnya logo berlatar transparan. Dua kartu lain memakai
         foto yang menutupi seluruh bidangnya, jadi latarnya tidak
         pernah tampak.

         ⚠️ KARTUNYA SENDIRI JUGA PUTIH. Tanpa garis pemisah, bidang
         gambar dan blok teks di bawahnya melebur jadi satu bidang
         putih - logo akan terlihat mengambang tanpa batas, dan kartu
         About jadi terbaca berbeda dari dua kartu bergambar foto yang
         batasnya tegas dengan sendirinya.

         border-b memakai warna garis yang sama dengan ring kartunya,
         jadi ia terbaca sebagai bagian dari bingkai, bukan sebagai
         garis tambahan.

         ⚠️ PADDINGNYA BARU BEKERJA SETELAH ADA PEMBUNGKUS DI DALAM -
         lihat catatan di sana. Sebelum itu, p-8 md:p-10 ini tidak
         berpengaruh apa pun. */}
      <div className={`relative flex aspect-[4/3] items-center justify-center overflow-hidden border-b border-[var(--c-line)] bg-[var(--c-surface)] ${kelasRuang}`}>
        {adaFoto ? (
          /* ⚠️ PEMBUNGKUS INI YANG MEMBUAT PADDING BEKERJA.

             next/image dengan `fill` memasang gambarnya sebagai
             elemen absolut ber-inset:0, dan elemen semacam itu
             diposisikan terhadap PADDING BOX induknya - bukan content
             box. Artinya padding pada induk TIDAK pernah menggesernya.

             p-8 md:p-10 di atas karena itu tidak berfungsi sejak
             awal: logo di kartu About tetap menyentuh tepi kiri-kanan
             meski paddingnya sudah ditulis, dan tidak ada galat apa
             pun yang memberi tahu.

             Dengan pembungkus ini, yang jadi acuan `fill` adalah
             kotak dalam - kotak yang memang sudah diperkecil oleh
             padding induknya.

             ⚠️ JANGAN BERI PADDING PADA PEMBUNGKUS INI. Kalau
             paddingnya dipindah ke sini, persoalan yang sama terulang
             satu tingkat lebih dalam. */
          <div className="relative h-full w-full">
            <Image
              src={bagian.image}
              fill
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 45vw, 92vw"
              alt=""
              onError={() => setGagal(true)}
              className={`transition-transform duration-500 ease-out motion-reduce:transition-none ${muatkan ? "object-contain group-hover:scale-[1.03]" : "object-cover group-hover:scale-105"}`}
            />
          </div>
        ) : (
          <Icon size={72} strokeWidth={1.25} className="text-[var(--c-brand-mid)] opacity-70" aria-hidden="true" />
        )}
      </div>
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <h2 className="mb-2 text-base font-bold leading-snug text-[var(--c-ink)] transition-colors duration-300 group-hover:text-[var(--c-brand)] motion-reduce:transition-none md:text-lg">
          {t(`header.tentang.${bagian.key}`)}
        </h2>

        <p className="flex-1 text-sm leading-relaxed text-[var(--c-body)]">
          {t(`header.tentang.${bagian.key}Desc`)}
        </p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--c-brand)] underline-offset-4 group-hover:underline">
          {t("umum.selengkapnya")}
          <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none">&rarr;</span>
        </span>
      </div>
    </Link>
  );
}
